import { getMonthly } from '@/api';
import { Box, Theme, Typography, useMediaQuery } from '@mui/material';
import { useRequest, useSize, useUpdate } from 'ahooks';
import moment from 'moment';
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { scaleLinear, scaleBand, ScaleBand, ScaleLinear } from 'd3-scale';
import { max as d3max } from 'd3-array';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';

type Item = {
	date: Date;
	volume: number;
};

function CustomTooltip({
	xScale,
	data,
	active,
}: { xScale: ScaleBand<Date>; data: Item[]; active?: Date }) {
	const item = useRef<Item | undefined>(undefined);
	const update = useUpdate();
	useEffect(() => {
		if (active) {
			item.current = data.find((d) => d.date.getTime() === active.getTime());
			update();
		}
	}, [data, active, update]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const labelFormatted = useMemo(
		() => (item.current ? moment(item.current.date).format('DD MMM') : ''),
		[item.current],
	);
	const containerRef = useRef<HTMLDivElement>(null);

	const pastPos = useRef<[string | number, boolean]>([0, false]);
	let left = pastPos.current[0];
	let leftSide = pastPos.current[1];
	const width = (containerRef?.current?.clientWidth ?? 200) + 20;

	const xRange = xScale.range()

	if (active) {
		left = xScale(active)!;
		if (left + width > xRange[1]) {
			leftSide = true;
		} else if (left - width < xRange[0]) {
			leftSide = false;
		}
		if (leftSide) {
			left -= 0.2 * xScale.step();
		} else {
			left += 1.2 * xScale.step();
		}
	}
	pastPos.current = [left, leftSide];
	const value = item.current?.volume;

	return (
		<div
			ref={containerRef}
			className='bg-backgroundPrimary border border-borderPrimary flex flex-col gap-0.5 p-3 rounded-xl'
			style={{
				opacity: active ? 1 : 0,
				position: 'absolute',
				pointerEvents: 'none',
				bottom: '15%',
				left: left,
				transform: leftSide ? 'translateX(-100%)' : undefined,
				transition: 'transform, left, opacity',
				transitionDuration: '150ms',
			}}
		>
			<Typography variant='appTinyRegular'>{labelFormatted}</Typography>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Typography variant='appSmallBold' color='common.textPrimary'>
					$
					{value?.toLocaleString('en-US', {
						maximumFractionDigits: 0,
					})}
				</Typography>
			</Box>
		</div>
	);
}

function Bars({
	data,
	xScale,
	yScale,
	active,
}: {
	data: Item[];
	xScale: ScaleBand<Date>;
	yScale: ScaleLinear<number, number>;
	active?: Date;
}) {
	const range = yScale.range();
	const yBound = Math.abs(range[1] - range[0]);

	return (
		<g>
			{data.map((d, index) => {
				const x = xScale(d.date)!;
				const y = yScale(d.volume);
				const height = range[0] - y;
				const width = xScale.bandwidth();

				return (
					<motion.rect
						x={x}
						width={width}
						initial={{ y: range[0], height: 0 }}
						animate={{ y, height }}
						transition={{ duration: (0.15 * height) / yBound, ease: 'circOut' }}
						key={index}
						fillOpacity={!active || d.date === active ? 1 : 0.4}
						style={{ fill: 'url(#MayanGradient)' }}
					/>
				);
			})}
		</g>
	);
}
const SizePrefixes = ' KMGTPEZYXWVU';
function getHumanSizeNumber(input: number) {
	if (input <= 0) return input;
	let rounding = 100_000;
	const maxPower = 5;
	const charLimit = 4;
	if (input < 1) {
		return Math.round(input * rounding) / rounding;
	}
	let power = Math.min(Math.round(Math.log(input) / Math.log(1000)), maxPower);
	let result: number;
	do {
		result = Math.round((input * rounding) / 1000 ** power) / rounding;
		if (power > 0 && Math.round(result * 10) < 10) {
			power -= 1;
			result = Math.round((input * rounding) / 1000 ** power) / rounding;
		}
		rounding = rounding / 10;
	} while (result.toString().length + (power ? 1 : 0) > charLimit);

	return result + SizePrefixes.charAt(power).replace(' ', '');
}

export default function Charts() {
	const { run, data: rawData } = useRequest(getMonthly);
	const svgRef = useRef<SVGSVGElement>(null);
	const theme = useTheme();

	useEffect(() => {
		run();
	}, [run]);

	const normalizedData: Item[] = useMemo(
		() =>
			rawData
				? rawData.days.slice(0, 30).map(({ date, mctpVolume, swiftVolume, whVolume }) => ({
					volume: mctpVolume + swiftVolume + whVolume,
					date: new Date(date * 1000),
				}))
				: Array(30)
					.fill(null)
					.map((_, i) => ({ volume: 0, date: moment().subtract(i, 'days').toDate() })),
		[rawData],
	);

	const total = normalizedData.reduce((a, b) => a + b.volume, 0);

	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	const margin = { top: 10, right: 10, bottom: 30, left: 30 };
	const { width, height } = useSize(svgRef.current) ?? { width: 1000, height: 300 };

	const yScale = useMemo(() => {
		return scaleLinear()
			.domain([0, rawData ? d3max(normalizedData, (d) => d.volume)! : 24_000_000])
			.nice()
			.range([height - margin.bottom, margin.top]);
	}, [normalizedData, height, rawData]);

	const xScale = useMemo(
		() =>
			scaleBand<Date>()
				.domain(normalizedData.map((d) => d.date))
				.range([margin.left, width - margin.right])
				.padding(0.1),
		[normalizedData, width],
	);

	const [active, setActive] = useState<Date | undefined>(undefined);

	const mouseOut = useCallback((_e: MouseEvent<SVGGElement>) => {
		setActive(undefined);
	}, []);
	const mouseMove = useCallback(
		(e: MouseEvent<SVGGElement>) => {
			const range = xScale.range();
			const nativeX = e.nativeEvent.offsetX;
			if (nativeX < range[0] || nativeX > range[1]) {
				return setActive(undefined);
			}
			const x = nativeX - range[0];
			const eachBand = xScale.step();
			const index = Math.floor(x / eachBand);
			const val = xScale.domain()[index];
			setActive(val);
		},
		[xScale],
	);

	return (
		<div className='flex items-stretch flex-col gap-4 p-4 sm:px-6 sm:py-6'>
			<div className='flex flex-col px-4 sm:px-0 sm:flex-row justify-between gap-4'>
				<div className='flex flex-col gap-1'>
					<Typography variant='appSmallRegular'>Past 30 days volume</Typography>
					<Typography variant='numsHeading' color='common.textPrimary'>
						${total.toLocaleString('en-US', { maximumFractionDigits: 0 })}
					</Typography>
				</div>
			</div>
			<div className='w-full h-80 relative select-none'>
				<CustomTooltip xScale={xScale} data={normalizedData} active={active} />
				<svg
					ref={svgRef}
					width='100%'
					height='100%'
					onMouseMove={mouseMove}
					onMouseEnter={mouseMove}
					onMouseOut={mouseOut}
				>
					<defs>
						<linearGradient id="MayanGradient" x1="0" x2="0" y1="1" y2="0">
							<stop offset={`${theme.palette.common.holographicGradient1Stops[0]}%`} stopColor="#FFFFFF" />
							<stop offset={`${theme.palette.common.holographicGradient1Stops[1]}%`} stopColor={theme.palette.common.gBlue} />
							<stop offset={`${theme.palette.common.holographicGradient1Stops[2]}%`} stopColor={theme.palette.common.gPink} />
							<stop offset={`${theme.palette.common.holographicGradient1Stops[3]}%`} stopColor={theme.palette.common.gYellow} />
						</linearGradient>
					</defs>
					<Bars data={normalizedData} xScale={xScale} yScale={yScale} active={active} />
					<g transform={`translate(0, ${yScale.range()[0]})`} textAnchor='middle' fill='none' fontSize={10}>
						{xScale
							.domain()
							.filter((_, i) => i % Math.floor(normalizedData.length / (isMobile ? 3 : 5)) === 0)
							.map((d, i) => (
								<g key={i} transform={`translate(${xScale(d)! + xScale.step() / 2 - 1},0)`}>
									<text className='fill-current' y={9} dy='1.2em'>
										{moment(d).format('DD MMM')}
									</text>
								</g>
							))}
					</g>
					<g textAnchor='middle' fill='none' fontSize={10} transform={`translate(${xScale.range()[0] / 2 - 3}, 0)`}>
						{yScale
							.ticks(6)
							.filter((d) => d)
							.map((d) => (
								<g key={d} transform={`translate(0, ${yScale(d)})`}>
									<text className='fill-current' dy='0.5em'>
										{getHumanSizeNumber(d)}
									</text>
								</g>
							))}
					</g>
				</svg>
			</div>
		</div>
	);
}
